import { Link } from "react-router-dom";

/**
 * Handles previous / next buttons.
 *
 * If `prevTo` / `nextTo` are given, `prevHandler` / `nextHandler` are ignored
 * and a `<Link>` element will be used instead of a `<button>`
 *
 * If neither `prevTo` / `nextTo` nor `prevHandler` / `nextHandler` are given,
 * a disabled `<button>` will be rendered.
 *
 * If `prevHide` / `nextHide` are true, the buttons will be rendered but hidden.
 * @param prevTo
 * @param nextTo
 * @param prevHandler
 * @param nextHandler
 * @param prevLabel
 * @param nextLabel
 * @param prevTitle
 * @param nextTitle
 * @param prevDisabled
 * @param nextDisabled
 * @param prevHide
 * @param nextHide
 * @constructor
 */
export const StepNavigation = ({
    prevTo = null,
    nextTo = null,
    prevHandler = null,
    nextHandler = null,
    nextDisabledHandler = null,
    prevDisabledHandler = null,
    prevLabel = null,
    nextLabel = null,
    prevTitle = null,
    nextTitle = null,
    prevDisabled = false,
    nextDisabled = false,
    prevHide = false,
    nextHide = false,
}) => {
    // Determine if prev and next buttons should be displayed
    const showPrev = prevTo || prevHandler || prevLabel || prevHide;
    const showNext = nextTo || nextHandler || nextLabel || nextHide;

    // Determine the container class based on which buttons are visible
    const containerClass =
        (!showPrev || !showNext) && !prevHide && !nextHide
            ? "step-navigation__inner--single"
            : "";

    function renderButton(
        to,
        handler,
        label,
        title,
        disabled,
        classSuffix,
        hide,
        disabledHandler,
    ) {
        const hiddenClass = hide ? "step-navigation__btn--hidden" : "";
        const disabledClass =
            disabled && disabledHandler ? "vnf-btn-quaternary--disabled" : "";

        if (disabled || (!to && !handler)) {
            return (
                <button
                    type="button"
                    disabled={disabled && !disabledHandler}
                    title={title || ""}
                    className={`${disabledClass} step-navigation__btn step-navigation__btn__${classSuffix} ${label ? "" : "step-navigation__btn__no-label"} ${hiddenClass}`}
                    onClick={
                        disabledHandler
                            ? (e) => {
                                  e.preventDefault();
                                  disabledHandler(e);
                              }
                            : undefined
                    }
                >
                    {label || ""}
                </button>
            );
        }

        if (typeof to === "string") {
            return (
                <Link
                    to={to || "/"}
                    title={title || ""}
                    className={`step-navigation__btn step-navigation__btn__${classSuffix} ${label ? "" : "step-navigation__btn__no-label"} ${hiddenClass}`}
                >
                    {label || ""}
                </Link>
            );
        } else {
            return (
                <button
                    type="button"
                    onClick={handler}
                    title={title || ""}
                    className={`step-navigation__btn step-navigation__btn__${classSuffix} ${label ? "" : "step-navigation__btn__no-label"} ${hiddenClass}`}
                >
                    {label || ""}
                </button>
            );
        }
    }

    return (
        <div className="step-navigation">
            <div className={`step-navigation__inner ${containerClass}`}>
                {(showPrev || prevHide) &&
                    renderButton(
                        prevTo,
                        prevHandler,
                        prevLabel,
                        prevTitle,
                        prevDisabled,
                        "prev",
                        prevHide,
                        prevDisabledHandler,
                    )}
                {(showNext || nextHide) &&
                    renderButton(
                        nextTo,
                        nextHandler,
                        nextLabel,
                        nextTitle,
                        nextDisabled,
                        "next",
                        nextHide,
                        nextDisabledHandler,
                    )}
            </div>
        </div>
    );
};
